import React, { useMemo, useState, useEffect } from 'react';
import { Card, Table, Button, Typography } from 'antd';
import { ArrowRightOutlined, DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useTableRenderDeferral } from '../../hooks/useTableRenderDeferral';
import TableLoadingPlaceholder from '../common/TableLoadingPlaceholder';
import { useTender } from '../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useNavigate } from 'react-router-dom';
import { COLORS, formatUnit, EVALUATE_CODES } from '../../utils/constants';
import { formatCurrency } from '../../utils/calculations';
import { useItemLibrary } from '../../hooks/useItemLibrary';
import MainLayout from '../layout/MainLayout';
import NumberInput from '../common/NumberInput';
import { ExcelImportModal } from '../common/ExcelImportExport';
import { ColumnConfigModal, ConfigSection, ConfigField, selectStyles } from '../common/ColumnConfigModal';
import type { FoulBuildUpRowData, CompoundConfig } from '../../types/tender.types';
import { COMPOUND_ROW_DEFS, CompoundRowType } from '../../utils/compoundRowDefs';
import { findCompoundEvaluateCode } from '../../utils/compoundEvaluateCodesMapping';
import { parseCompoundFromExcel } from '../../utils/compoundExcelUtils';

const DEFAULT_COMPOUND_SIZE_W = 0;
const DEFAULT_COMPOUND_SIZE_H = 0;
const DEFAULT_SILO_BASE_SIZE_W = 0;
const DEFAULT_SILO_BASE_SIZE_H = 0;
const DEFAULT_CARPARK_SIZE_W = 0;
const DEFAULT_CARPARK_SIZE_H = 0;
const DEFAULT_MATERIAL_STORE_SIZE_W = 0;
const DEFAULT_MATERIAL_STORE_SIZE_H = 0;
const DEFAULT_SUPPLY_LAY_6F2_MM = 0;
const DEFAULT_TYPE1_MM = 0;
const DEFAULT_CONCRETE_GRADE = '';
const DEFAULT_CONCRETE_MM = 0;
const DEFAULT_MESH_TYPE = '';
const DEFAULT_SILO_SUB_BASE_MM = 0;
const DEFAULT_SILO_BLINDING_MM = 0;
const DEFAULT_SILO_CONCRETE_GRADE = '';
const DEFAULT_SILO_CONCRETE_MM = 0;
const DEFAULT_SILO_MESH_TYPE = '';
const DEFAULT_SILO_OGCR_MM = 0;
const DEFAULT_CARPARK_SUPPLY_LAY_6F2_MM = 0;
const DEFAULT_CARPARK_TYPE1_MM = 0;
const DEFAULT_MATERIAL_STORE_SUPPLY_LAY_6F2_MM = 0;
const DEFAULT_MATERIAL_STORE_TYPE1_MM = 0;
const DEFAULT_MATERIAL_STORE_CONCRETE_GRADE = '';
const DEFAULT_MATERIAL_STORE_CONCRETE_MM = 0;
const DEFAULT_MATERIAL_STORE_MESH_TYPE = '';

// Same option lists as /groundworks/foundations' "Concrete to Strip" and
// "Mesh Reinforcement" dropdowns.
const CONCRETE_GRADE_OPTIONS = ['GEN1', 'GEN2', 'GEN3', 'FND2', 'FND3z', 'FND4', 'RC28','RC35', 'RC32','RC40', 'C16','C20'];
const MESH_TYPE_OPTIONS = ['A142', 'A193', 'A252', 'A393', 'B283', 'B385', 'B503', 'B785', 'B1131'];

const DEFAULT_CARPARK_BINDERCOURSE_MM = 0;

// Same option set as /groundworks/plot-drives' "xxmm DB Binder" dropdown.
const BINDERCOURSE_OPTIONS: { label: string; mm: number }[] = [
  { label: '50mm thick AC 20mm dense bin', mm: 50 },
  { label: '60mm thick AC 20mm dense bin', mm: 60 },
  { label: '75mm thick AC 20mm dense bin', mm: 75 },
  { label: '80mm thick AC 20mm dense bin', mm: 80 },
  { label: '100mm thick AC 20mm dense bin', mm: 100 },
];

// Same option sets as /groundworks/plot-drives' "xxmm DB Base"/"xxmm DB
// Binder"/"xxmm DB Surface course" dropdowns, for SALES AREA's "Sales
// Parking, tarmac drive" rows.
const SALES_BASE_COURSE_OPTIONS = ['100mm thick AC 32mm dense bin', '150mm thick AC 32mm dense bin'];
const SALES_BINDER_COURSE_OPTIONS = BINDERCOURSE_OPTIONS.map((opt) => opt.label);
const SALES_SURFACE_COURSE_OPTIONS = [
  '20mm thick AC 6mm dense surf',
  '25mm thick AC 6mm dense surf',
  '25mm thick AC 10mm dense surf',
  '30mm thick AC 6mm dense surf',
  '30mm thick AC 10mm dense surf',
];
// Same option set as /groundworks/plot-drives' Block Paved surface's "60mm/80mm
// Concrete paviors" dropdowns (60mmBlockPaviors + 80mmBlockPaviors combined).
const SALES_BLOCK_PAVED_FOOTPATHS_OPTIONS = Object.values(EVALUATE_CODES)
  .filter((layer) => ['GWALPD002', 'GWALPD010', 'GWALPD006', 'GWALPD001', 'GWALPD009', 'GWALPD005'].includes(layer.code))
  .map((layer) => layer.label);
const DEFAULT_SALES_BASE_COURSE = '';
const DEFAULT_SALES_BINDER_COURSE = '';
const DEFAULT_SALES_SURFACE_COURSE = '';
const DEFAULT_SALES_BLOCK_PAVED_FOOTPATHS = '';
const DEFAULT_SALES_TOPSOILING_MM = 0;
const DEFAULT_THR_SUPPLY_LAY_6F2_MM = 0;
const DEFAULT_WK_SUPPLY_LAY_6F2_MM = 0;


// Tree node for Ant Design Table expandable rows
interface CompoundTreeNode {
  key: string;
  id: string;
  rowType: CompoundRowType;
  description: string;
  unit: string;
  itemNo: number | null;
  children?: CompoundTreeNode[];
}

// Build the two-level tree from COMPOUND_ROW_DEFS once at module level
// compoundHeader = top-level parent; sectionHeader = child section within parent
const buildTree = (): { tree: CompoundTreeNode[]; allExpandKeys: React.Key[] } => {
  const tree: CompoundTreeNode[] = [];
  const allExpandKeys: React.Key[] = [];
  let currentParent: CompoundTreeNode | null = null;
  let currentSection: CompoundTreeNode | null = null;

  for (const row of COMPOUND_ROW_DEFS) {
    if (row.rowType === 'compoundHeader') {
      const node: CompoundTreeNode = { key: row.id, id: row.id, rowType: row.rowType, description: row.description, unit: row.unit, itemNo: row.itemNo, children: [] };
      tree.push(node);
      allExpandKeys.push(row.id);
      currentParent = node;
      currentSection = null;
    } else if (row.rowType === 'sectionHeader') {
      const node: CompoundTreeNode = { key: row.id, id: row.id, rowType: row.rowType, description: row.description, unit: row.unit, itemNo: row.itemNo, children: [] };
      currentParent?.children?.push(node);
      allExpandKeys.push(row.id);
      currentSection = node;
    } else if (row.rowType !== 'empty') {
      // data and subHeader rows — skip empty spacers in tree view
      const node: CompoundTreeNode = { key: row.id, id: row.id, rowType: row.rowType, description: row.description, unit: row.unit, itemNo: row.itemNo };
      if (currentSection) {
        currentSection.children?.push(node);
      } else {
        currentParent?.children?.push(node);
      }
    }
  }

  return { tree, allExpandKeys };
};

const { tree: COMPOUND_TREE, allExpandKeys: ALL_EXPAND_KEYS } = buildTree();

const Compound: React.FC = () => {
  const showDevMode = useDevMode();
  useScrollToTop({ behavior: 'smooth', delay: 100 });
  const { state, updateField, saveTender } = useTender();
  const navigate = useNavigate();
  useAutoSave();

  // Item library for dev-mode rate lookups by evaluate code — only fetched
  // when the dev-only Evaluate Code column is actually shown.
  const { data: itemLibraryData } = useItemLibrary(state.region || "", undefined, {
    enabled: showDevMode,
  });
  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item.nettRate || item.baseRate || 0);
    });
    return map;
  }, [itemLibraryData]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return resourceCodeMap.get(code.trim().toUpperCase());
  };

  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [stickyOffset, setStickyOffset] = useState(230);
  // Defers the Compound table's first mount by one paint cycle so this step
  // doesn't look frozen while the table's initial render/layout (every row
  // always expanded via expandedRowKeys) blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [configDraft, setConfigDraft] = useState<CompoundConfig>({
    compoundSizeW: DEFAULT_COMPOUND_SIZE_W,
    compoundSizeH: DEFAULT_COMPOUND_SIZE_H,
    siloBaseSizeW: DEFAULT_SILO_BASE_SIZE_W,
    siloBaseSizeH: DEFAULT_SILO_BASE_SIZE_H,
    carparkSizeW: DEFAULT_CARPARK_SIZE_W,
    carparkSizeH: DEFAULT_CARPARK_SIZE_H,
    materialStoreSizeW: DEFAULT_MATERIAL_STORE_SIZE_W,
    materialStoreSizeH: DEFAULT_MATERIAL_STORE_SIZE_H,
    compoundSupplyLay6F2Mm: DEFAULT_SUPPLY_LAY_6F2_MM,
    compoundType1Mm: DEFAULT_TYPE1_MM,
    compoundConcreteGrade: DEFAULT_CONCRETE_GRADE,
    compoundConcreteMm: DEFAULT_CONCRETE_MM,
    compoundMeshType: DEFAULT_MESH_TYPE,
    siloSubBaseMm: DEFAULT_SILO_SUB_BASE_MM,
    siloBlindingMm: DEFAULT_SILO_BLINDING_MM,
    siloConcreteGrade: DEFAULT_SILO_CONCRETE_GRADE,
    siloConcreteMm: DEFAULT_SILO_CONCRETE_MM,
    siloMeshType: DEFAULT_SILO_MESH_TYPE,
    siloOgcrMm: DEFAULT_SILO_OGCR_MM,
    carparkSupplyLay6F2Mm: DEFAULT_CARPARK_SUPPLY_LAY_6F2_MM,
    carparkType1Mm: DEFAULT_CARPARK_TYPE1_MM,
    carparkBindercourseMm: DEFAULT_CARPARK_BINDERCOURSE_MM,
    materialStoreSupplyLay6F2Mm: DEFAULT_MATERIAL_STORE_SUPPLY_LAY_6F2_MM,
    materialStoreType1Mm: DEFAULT_MATERIAL_STORE_TYPE1_MM,
    materialStoreConcreteGrade: DEFAULT_MATERIAL_STORE_CONCRETE_GRADE,
    materialStoreConcreteMm: DEFAULT_MATERIAL_STORE_CONCRETE_MM,
    materialStoreMeshType: DEFAULT_MATERIAL_STORE_MESH_TYPE,
    salesParkingBaseCourseLabel: DEFAULT_SALES_BASE_COURSE,
    salesParkingBinderCourseLabel: DEFAULT_SALES_BINDER_COURSE,
    salesParkingSurfaceCourseLabel: DEFAULT_SALES_SURFACE_COURSE,
    salesParkingBlockPavedFootpathsLabel: DEFAULT_SALES_BLOCK_PAVED_FOOTPATHS,
    salesParkingTopsoilingMm: DEFAULT_SALES_TOPSOILING_MM,
    temporaryHaulRoadSupplyLay6F2Mm: DEFAULT_THR_SUPPLY_LAY_6F2_MM,
    walkwaysSupplyLay6F2Mm: DEFAULT_WK_SUPPLY_LAY_6F2_MM,
  });

  useEffect(() => {
    const el = document.querySelector('.compound-sticky-header') as HTMLElement | null;
    if (el) setStickyOffset(144 + el.offsetHeight);
  }, []);

  const savedData = useMemo(() => state.compound?.table ?? {}, [state.compound]);
  const savedConfig = (state.compound?.config ?? {}) as CompoundConfig;
  const compoundSizeW = savedConfig.compoundSizeW ?? DEFAULT_COMPOUND_SIZE_W;
  const compoundSizeH = savedConfig.compoundSizeH ?? DEFAULT_COMPOUND_SIZE_H;
  const siloBaseSizeW = savedConfig.siloBaseSizeW ?? DEFAULT_SILO_BASE_SIZE_W;
  const siloBaseSizeH = savedConfig.siloBaseSizeH ?? DEFAULT_SILO_BASE_SIZE_H;
  const carparkSizeW = savedConfig.carparkSizeW ?? DEFAULT_CARPARK_SIZE_W;
  const carparkSizeH = savedConfig.carparkSizeH ?? DEFAULT_CARPARK_SIZE_H;
  const materialStoreSizeW = savedConfig.materialStoreSizeW ?? DEFAULT_MATERIAL_STORE_SIZE_W;
  const materialStoreSizeH = savedConfig.materialStoreSizeH ?? DEFAULT_MATERIAL_STORE_SIZE_H;
  const compoundSupplyLay6F2Mm = savedConfig.compoundSupplyLay6F2Mm ?? DEFAULT_SUPPLY_LAY_6F2_MM;
  const compoundType1Mm = savedConfig.compoundType1Mm ?? DEFAULT_TYPE1_MM;
  const compoundConcreteGrade = savedConfig.compoundConcreteGrade ?? DEFAULT_CONCRETE_GRADE;
  const compoundConcreteMm = savedConfig.compoundConcreteMm ?? DEFAULT_CONCRETE_MM;
  const compoundMeshType = savedConfig.compoundMeshType ?? DEFAULT_MESH_TYPE;
  const siloSubBaseMm = savedConfig.siloSubBaseMm ?? DEFAULT_SILO_SUB_BASE_MM;
  const siloBlindingMm = savedConfig.siloBlindingMm ?? DEFAULT_SILO_BLINDING_MM;
  const siloConcreteGrade = savedConfig.siloConcreteGrade ?? DEFAULT_SILO_CONCRETE_GRADE;
  const siloConcreteMm = savedConfig.siloConcreteMm ?? DEFAULT_SILO_CONCRETE_MM;
  const siloMeshType = savedConfig.siloMeshType ?? DEFAULT_SILO_MESH_TYPE;
  const siloOgcrMm = savedConfig.siloOgcrMm ?? DEFAULT_SILO_OGCR_MM;
  const carparkSupplyLay6F2Mm = savedConfig.carparkSupplyLay6F2Mm ?? DEFAULT_CARPARK_SUPPLY_LAY_6F2_MM;
  const carparkType1Mm = savedConfig.carparkType1Mm ?? DEFAULT_CARPARK_TYPE1_MM;
  const carparkBindercourseMm = savedConfig.carparkBindercourseMm ?? DEFAULT_CARPARK_BINDERCOURSE_MM;
  const carparkBindercourseLabel = savedConfig.carparkBindercourseLabel;
  const materialStoreSupplyLay6F2Mm = savedConfig.materialStoreSupplyLay6F2Mm ?? DEFAULT_MATERIAL_STORE_SUPPLY_LAY_6F2_MM;
  const materialStoreType1Mm = savedConfig.materialStoreType1Mm ?? DEFAULT_MATERIAL_STORE_TYPE1_MM;
  const materialStoreConcreteGrade = savedConfig.materialStoreConcreteGrade ?? DEFAULT_MATERIAL_STORE_CONCRETE_GRADE;
  const materialStoreConcreteMm = savedConfig.materialStoreConcreteMm ?? DEFAULT_MATERIAL_STORE_CONCRETE_MM;
  const materialStoreMeshType = savedConfig.materialStoreMeshType ?? DEFAULT_MATERIAL_STORE_MESH_TYPE;
  const salesParkingBaseCourseLabel = savedConfig.salesParkingBaseCourseLabel ?? DEFAULT_SALES_BASE_COURSE;
  const salesParkingBinderCourseLabel = savedConfig.salesParkingBinderCourseLabel ?? DEFAULT_SALES_BINDER_COURSE;
  const salesParkingSurfaceCourseLabel = savedConfig.salesParkingSurfaceCourseLabel ?? DEFAULT_SALES_SURFACE_COURSE;
  const salesParkingBlockPavedFootpathsLabel = savedConfig.salesParkingBlockPavedFootpathsLabel ?? DEFAULT_SALES_BLOCK_PAVED_FOOTPATHS;
  const salesParkingTopsoilingMm = savedConfig.salesParkingTopsoilingMm ?? DEFAULT_SALES_TOPSOILING_MM;
  const temporaryHaulRoadSupplyLay6F2Mm = savedConfig.temporaryHaulRoadSupplyLay6F2Mm ?? DEFAULT_THR_SUPPLY_LAY_6F2_MM;
  const walkwaysSupplyLay6F2Mm = savedConfig.walkwaysSupplyLay6F2Mm ?? DEFAULT_WK_SUPPLY_LAY_6F2_MM;

  const openConfigModal = () => {
    setConfigDraft({
      compoundSizeW: savedConfig.compoundSizeW ?? DEFAULT_COMPOUND_SIZE_W,
      compoundSizeH: savedConfig.compoundSizeH ?? DEFAULT_COMPOUND_SIZE_H,
      siloBaseSizeW: savedConfig.siloBaseSizeW ?? DEFAULT_SILO_BASE_SIZE_W,
      siloBaseSizeH: savedConfig.siloBaseSizeH ?? DEFAULT_SILO_BASE_SIZE_H,
      carparkSizeW: savedConfig.carparkSizeW ?? DEFAULT_CARPARK_SIZE_W,
      carparkSizeH: savedConfig.carparkSizeH ?? DEFAULT_CARPARK_SIZE_H,
      materialStoreSizeW: savedConfig.materialStoreSizeW ?? DEFAULT_MATERIAL_STORE_SIZE_W,
      materialStoreSizeH: savedConfig.materialStoreSizeH ?? DEFAULT_MATERIAL_STORE_SIZE_H,
      compoundSupplyLay6F2Mm: savedConfig.compoundSupplyLay6F2Mm ?? DEFAULT_SUPPLY_LAY_6F2_MM,
      compoundType1Mm: savedConfig.compoundType1Mm ?? DEFAULT_TYPE1_MM,
      compoundConcreteGrade: savedConfig.compoundConcreteGrade ?? DEFAULT_CONCRETE_GRADE,
      compoundConcreteMm: savedConfig.compoundConcreteMm ?? DEFAULT_CONCRETE_MM,
      compoundMeshType: savedConfig.compoundMeshType ?? DEFAULT_MESH_TYPE,
      siloSubBaseMm: savedConfig.siloSubBaseMm ?? DEFAULT_SILO_SUB_BASE_MM,
      siloBlindingMm: savedConfig.siloBlindingMm ?? DEFAULT_SILO_BLINDING_MM,
      siloConcreteGrade: savedConfig.siloConcreteGrade ?? DEFAULT_SILO_CONCRETE_GRADE,
      siloConcreteMm: savedConfig.siloConcreteMm ?? DEFAULT_SILO_CONCRETE_MM,
      siloMeshType: savedConfig.siloMeshType ?? DEFAULT_SILO_MESH_TYPE,
      siloOgcrMm: savedConfig.siloOgcrMm ?? DEFAULT_SILO_OGCR_MM,
      carparkSupplyLay6F2Mm: savedConfig.carparkSupplyLay6F2Mm ?? DEFAULT_CARPARK_SUPPLY_LAY_6F2_MM,
      carparkType1Mm: savedConfig.carparkType1Mm ?? DEFAULT_CARPARK_TYPE1_MM,
      carparkBindercourseMm: savedConfig.carparkBindercourseMm ?? DEFAULT_CARPARK_BINDERCOURSE_MM,
      carparkBindercourseLabel: savedConfig.carparkBindercourseLabel,
      materialStoreSupplyLay6F2Mm: savedConfig.materialStoreSupplyLay6F2Mm ?? DEFAULT_MATERIAL_STORE_SUPPLY_LAY_6F2_MM,
      materialStoreType1Mm: savedConfig.materialStoreType1Mm ?? DEFAULT_MATERIAL_STORE_TYPE1_MM,
      materialStoreConcreteGrade: savedConfig.materialStoreConcreteGrade ?? DEFAULT_MATERIAL_STORE_CONCRETE_GRADE,
      materialStoreConcreteMm: savedConfig.materialStoreConcreteMm ?? DEFAULT_MATERIAL_STORE_CONCRETE_MM,
      materialStoreMeshType: savedConfig.materialStoreMeshType ?? DEFAULT_MATERIAL_STORE_MESH_TYPE,
      salesParkingBaseCourseLabel: savedConfig.salesParkingBaseCourseLabel ?? DEFAULT_SALES_BASE_COURSE,
      salesParkingBinderCourseLabel: savedConfig.salesParkingBinderCourseLabel ?? DEFAULT_SALES_BINDER_COURSE,
      salesParkingSurfaceCourseLabel: savedConfig.salesParkingSurfaceCourseLabel ?? DEFAULT_SALES_SURFACE_COURSE,
      salesParkingBlockPavedFootpathsLabel: savedConfig.salesParkingBlockPavedFootpathsLabel ?? DEFAULT_SALES_BLOCK_PAVED_FOOTPATHS,
      salesParkingTopsoilingMm: savedConfig.salesParkingTopsoilingMm ?? DEFAULT_SALES_TOPSOILING_MM,
      temporaryHaulRoadSupplyLay6F2Mm: savedConfig.temporaryHaulRoadSupplyLay6F2Mm ?? DEFAULT_THR_SUPPLY_LAY_6F2_MM,
      walkwaysSupplyLay6F2Mm: savedConfig.walkwaysSupplyLay6F2Mm ?? DEFAULT_WK_SUPPLY_LAY_6F2_MM,
    });
    setConfigModalVisible(true);
  };

  const applyConfig = () => {
    updateField('compound', { ...state.compound, table: savedData, config: { ...configDraft } });
    setConfigModalVisible(false);
  };

  const getRow = (rowId: string): FoulBuildUpRowData =>
    savedData[rowId] ?? { quant: null, evaluateCode: '' };

  const updateRow = (rowId: string, changes: Partial<FoulBuildUpRowData>) => {
    const current = savedData[rowId] ?? { quant: null, evaluateCode: '' };
    updateField('compound', { ...state.compound, table: { ...savedData, [rowId]: { ...current, ...changes } } });
  };

  const columns = useMemo(() => [
    {
      title: 'Item',
      key: 'itemNo',
      width: 60,
      align: 'center' as const,
      fixed: 'left' as const,
      render: (_: unknown, record: CompoundTreeNode) => {
        if (record.rowType !== 'data') return null;
        return record.itemNo !== null ? (
          <span style={{ color: '#000000', fontSize: 12 }}>{record.itemNo}</span>
        ) : null;
      },
    },
    {
      title: 'Description',
      key: 'description',
      width: 480,
      fixed: 'left' as const,
      render: (_: unknown, record: CompoundTreeNode) => {
        if (record.rowType === 'compoundHeader') {
          return (
            <Typography.Text strong className="text-indigo-600">
              {record.description}
            </Typography.Text>
          );
        }
        // Unconfigured (0 / '') fields show an "xx"/"xxx" placeholder in the
        // item description instead of a literal 0 or blank, matching the
        // static placeholder wording convention used elsewhere (e.g. posRowDefs.ts).
        const mPh = (val: number, xs = 'xx') => (val > 0 ? `${val}m` : `${xs}m`);
        const mmPh = (val: number, xs = 'xxx') => (val > 0 ? `${val}mm` : `${xs}mm`);
        const strPh = (s: string | undefined) => (s && s.length > 0 ? s : 'xxx');
        if (record.rowType === 'sectionHeader') {
          const label =
            record.id === 'cw_sub' ? `Compound ${mPh(compoundSizeW)} x ${mPh(compoundSizeH)}` :
            record.id === 'sb_hdr' ? `Silo Base, ${mPh(siloBaseSizeW)} x ${mPh(siloBaseSizeH)}` :
            record.id === 'cp_hdr' ? `Carpark ${mPh(carparkSizeW)} x ${mPh(carparkSizeH)}` :
            record.id === 'ms_hdr' ? `Material store ${mPh(materialStoreSizeW)} x ${mPh(materialStoreSizeH)}` :
            record.description;
          return (
            <Typography.Text strong className="text-indigo-600">
              {label}
            </Typography.Text>
          );
        }
        if (record.rowType === 'subHeader') {
          return (
            <Typography.Text strong className="text-indigo-600">
              {record.description}
            </Typography.Text>
          );
        }
        const desc =
          record.id === 'cw_3' ? `Supply & Lay 6F2, ${mmPh(compoundSupplyLay6F2Mm)} thick` :
          record.id === 'cw_4' ? `Type 1, ${mmPh(compoundType1Mm)} thick` :
          record.id === 'cw_11' ? `${strPh(compoundConcreteGrade)} concrete, ${mmPh(compoundConcreteMm)} thick with 1 layer ${strPh(compoundMeshType)} mesh` :
          record.id === 'sb_2' ? `${mmPh(siloSubBaseMm)} 6F2 Sub Base` :
          record.id === 'sb_3' ? `${mmPh(siloBlindingMm, 'xx')} Concrete Blinding` :
          record.id === 'sb_5' ? `${strPh(siloConcreteGrade)} ${mmPh(siloConcreteMm)} thick` :
          record.id === 'sb_6' ? `2 x layers ${strPh(siloMeshType)} Mesh` :
          record.id === 'sb_7' ? `40-70mm OGCR ${mmPh(siloOgcrMm)} thick` :
          record.id === 'cp_3' ? `Supply & Lay 6F2, ${mmPh(carparkSupplyLay6F2Mm)} thick` :
          record.id === 'cp_4' ? `Supply and lay Type 1, ${mmPh(carparkType1Mm)} thick` :
          record.id === 'cp_5' ? `Bindercourse, ${carparkBindercourseLabel ?? BINDERCOURSE_OPTIONS.find((o) => o.mm === carparkBindercourseMm)?.label ?? `${mmPh(carparkBindercourseMm, 'xx')} thick`}` :
          record.id === 'ms_3' ? `Supply & Lay 6F2, ${mmPh(materialStoreSupplyLay6F2Mm)} thick` :
          record.id === 'ms_4' ? `Supply and lay Type 1, ${mmPh(materialStoreType1Mm)} thick` :
          record.id === 'ms_5' ? `${strPh(materialStoreConcreteGrade)} concrete, ${mmPh(materialStoreConcreteMm)} thick with 1 layer ${strPh(materialStoreMeshType)} mesh` :
          record.id === 'sa_2' ? `Base Course, ${strPh(salesParkingBaseCourseLabel)}` :
          record.id === 'sa_3' ? `Binder Course, ${strPh(salesParkingBinderCourseLabel)}` :
          record.id === 'sa_4' ? `Surface Course, ${strPh(salesParkingSurfaceCourseLabel)}` :
          record.id === 'sa_6' ? `Blockpaved footpaths, ${strPh(salesParkingBlockPavedFootpathsLabel)}` :
          record.id === 'sa_8' ? `Topsoiling, ${mmPh(salesParkingTopsoilingMm)} thick, free issue site won` :
          record.id === 'thr_3' ? `Supply and lay 6F2, ${mmPh(temporaryHaulRoadSupplyLay6F2Mm)} thick` :
          record.id === 'wk_3' ? `Supply and lay 6F2, ${mmPh(walkwaysSupplyLay6F2Mm)} thick` :
          record.description;
        return <Typography.Text>{desc}</Typography.Text>;
      },
    },
    {
      title: 'Qty',
      key: 'qty',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: CompoundTreeNode) => {
        if (record.rowType !== 'data') return null;
        const row = getRow(record.id);
        return (
          <NumberInput
            value={row.quant ?? 0}
            onChange={(v) => updateRow(record.id, { quant: v })}
            decimalPlaces={3}
            positiveOnly
            placeholder="0.000"
            style={{ width: '100%', textAlign: 'right' }}
          />
        );
      },
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (_: unknown, record: CompoundTreeNode) => {
        if (!record.unit) return null;
        return <span style={{ color: '#6b7280' }}>{formatUnit(record.unit)}</span>;
      },
    },
    ...(showDevMode ? [{
      title: 'Evaluate Code',
      key: 'evaluateCode',
      width: 160,
      render: (_: unknown, record: CompoundTreeNode) => {
        if (record.rowType !== 'data') return null;
        // `getRow(record.id).evaluateCode` is never actually persisted — `updateRow` only
        // ever writes `quant` — so this always read as blank. Resolved live instead, same
        // as every other build-up domain's dev-mode column (though the source spec's
        // 'Compound' sheet has no codes assigned to any row yet, so this currently always
        // returns undefined).
        const evaluateCode = findCompoundEvaluateCode(record.description);
        const libRate = getLibRate(evaluateCode);
        return (
          <span>
            <span style={{ color: '#6b7280', fontSize: 12 }}>{evaluateCode}</span>
            {libRate !== undefined && (
              <div style={{ color: '#52c41a', fontSize: 12, marginTop: 2 }}>
                {formatCurrency(libRate)}
              </div>
            )}
          </span>
        );
      },
    }] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [savedData, compoundSizeW, compoundSizeH, siloBaseSizeW, siloBaseSizeH, carparkSizeW, carparkSizeH, materialStoreSizeW, materialStoreSizeH, compoundSupplyLay6F2Mm, compoundType1Mm, compoundConcreteGrade, compoundConcreteMm, compoundMeshType, siloSubBaseMm, siloBlindingMm, siloConcreteGrade, siloConcreteMm, siloMeshType, siloOgcrMm, carparkSupplyLay6F2Mm, carparkType1Mm, carparkBindercourseMm, carparkBindercourseLabel, materialStoreSupplyLay6F2Mm, materialStoreType1Mm, materialStoreConcreteGrade, materialStoreConcreteMm, materialStoreMeshType, salesParkingBaseCourseLabel, salesParkingBinderCourseLabel, salesParkingSurfaceCourseLabel, salesParkingBlockPavedFootpathsLabel, salesParkingTopsoilingMm, temporaryHaulRoadSupplyLay6F2Mm, walkwaysSupplyLay6F2Mm, showDevMode, resourceCodeMap]);

  const handleImport = async (file: File) => {
    setImportLoading(true);
    try {
      const imported = await parseCompoundFromExcel(file);
      updateField('compound', { ...state.compound, table: imported.table, config: imported.config });
      setImportModalVisible(false);
      const count = Object.keys(imported.table).length;
      if (count === 0) {
        alert('No matching rows found in "Compound" sheet. Check that the sheet exists and section headers are present.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('Compound import failed:', err);
      alert(`Import failed: ${msg}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleSaveAndContinue = () => {
    updateField("status", "In-progress");
    updateField("boqStatus", "In-progress");
    updateField("lastModified", new Date());
    saveTender();
    navigate('/roads-sewers/review');
  };

  return (
    <MainLayout showFooter={false}>
      <Card
        className="compound-card shadow-sm"
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: '8px', overflow: 'visible' }}
      >
        {/* Sticky header */}
        <div className="compound-sticky-header" style={{ position: 'sticky', top: '144px', backgroundColor: '#fff', zIndex: 98, borderRadius: '8px 8px 0 0', isolation: 'isolate' }}>
        <div className="px-6 pt-6 pb-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex justify-between items-center">
            <div>
              <Typography.Title level={3} style={{ fontWeight: 600, fontSize: '20px', color: '#111827', marginBottom: '8px' }}>
                Compound
              </Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: '14px', color: '#6B7280' }}>
                Compound works quantities
              </Typography.Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                size="small"
                icon={<SettingOutlined />}
                onClick={openConfigModal}
                type="default"
                className="configure-headers-btn"
                style={{
                  marginTop:"16px",
                  height: '32px',
                  borderRadius: '6px',
                  fontWeight: 500,
                  borderColor: '#1890ff',
                  color: '#1890ff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Configure
              </Button>
              {showDevMode && (
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => setImportModalVisible(true)}
                  style={{ color: '#1890ff', fontWeight: 500 }}
                >
                  Import Take-off
                </Button>
              )}
            </div>
          </div>
        </div>
        </div>{/* end compound-sticky-header */}

        {/* Content */}
        <div className="px-6 py-6">
          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
            <Table<CompoundTreeNode>
              bordered
              size="middle"
              scroll={{ x: 900 }}
              pagination={false}
              dataSource={COMPOUND_TREE}
              rowKey="key"
              sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
              expandable={{
                childrenColumnName: 'children',
                expandedRowKeys: ALL_EXPAND_KEYS,
                expandIcon: () => null,
              }}
              rowClassName={(record) => {
                if (record.rowType === 'compoundHeader') return 'compound-row-parent';
                if (record.rowType === 'sectionHeader') return 'compound-row-section';
                if (record.rowType === 'subHeader') return 'compound-row-sub';
                return '';
              }}
              columns={columns}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{ backgroundColor: COLORS.primary, borderColor: COLORS.primary }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      </Card>

      <ExcelImportModal
        onImport={handleImport}
        onDownloadTemplate={() => {/* template not needed */}}
        importLoading={importLoading}
        importModalVisible={importModalVisible}
        setImportModalVisible={setImportModalVisible}
      />

      <ColumnConfigModal
        title="Configure"
        open={configModalVisible}
        onClose={() => setConfigModalVisible(false)}
        onApply={applyConfig}
        width={800}
        isViewMode={state.isViewMode}
      >
        <ConfigSection title="Compound" columns={4}>
          <ConfigField label="Compound size — Width (m)">
            <NumberInput
              value={configDraft.compoundSizeW ?? DEFAULT_COMPOUND_SIZE_W}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, compoundSizeW: v ?? DEFAULT_COMPOUND_SIZE_W }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Compound size — Height (m)">
            <NumberInput
              value={configDraft.compoundSizeH ?? DEFAULT_COMPOUND_SIZE_H}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, compoundSizeH: v ?? DEFAULT_COMPOUND_SIZE_H }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply & Lay 6F2, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.compoundSupplyLay6F2Mm ?? DEFAULT_SUPPLY_LAY_6F2_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, compoundSupplyLay6F2Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Type 1, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.compoundType1Mm ?? DEFAULT_TYPE1_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, compoundType1Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Grade">
            <select
              value={configDraft.compoundConcreteGrade ?? DEFAULT_CONCRETE_GRADE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, compoundConcreteGrade: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="Concrete, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.compoundConcreteMm ?? DEFAULT_CONCRETE_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, compoundConcreteMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <select
              value={configDraft.compoundMeshType ?? DEFAULT_MESH_TYPE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, compoundMeshType: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Silo Base" columns={4}>
          <ConfigField label="Silo Base size — Width (m)">
            <NumberInput
              value={configDraft.siloBaseSizeW ?? DEFAULT_SILO_BASE_SIZE_W}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, siloBaseSizeW: v ?? DEFAULT_SILO_BASE_SIZE_W }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Silo Base size — Height (m)">
            <NumberInput
              value={configDraft.siloBaseSizeH ?? DEFAULT_SILO_BASE_SIZE_H}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, siloBaseSizeH: v ?? DEFAULT_SILO_BASE_SIZE_H }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="xxxmm 6F2 Sub Base">
            <NumberInput
              value={(() => {
                const mm = configDraft.siloSubBaseMm ?? DEFAULT_SILO_SUB_BASE_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, siloSubBaseMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="xxxmm Concrete Blinding">
            <NumberInput
              value={(() => {
                const mm = configDraft.siloBlindingMm ?? DEFAULT_SILO_BLINDING_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, siloBlindingMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Grade">
            <select
              value={configDraft.siloConcreteGrade ?? DEFAULT_SILO_CONCRETE_GRADE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, siloConcreteGrade: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="Concrete, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.siloConcreteMm ?? DEFAULT_SILO_CONCRETE_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, siloConcreteMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <select
              value={configDraft.siloMeshType ?? DEFAULT_SILO_MESH_TYPE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, siloMeshType: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="40-70mm OGCR, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.siloOgcrMm ?? DEFAULT_SILO_OGCR_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, siloOgcrMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Carpark" columns={4}>
          <ConfigField label="Carpark size — Width (m)">
            <NumberInput
              value={configDraft.carparkSizeW ?? DEFAULT_CARPARK_SIZE_W}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, carparkSizeW: v ?? DEFAULT_CARPARK_SIZE_W }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Carpark size — Height (m)">
            <NumberInput
              value={configDraft.carparkSizeH ?? DEFAULT_CARPARK_SIZE_H}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, carparkSizeH: v ?? DEFAULT_CARPARK_SIZE_H }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply & Lay 6F2, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.carparkSupplyLay6F2Mm ?? DEFAULT_CARPARK_SUPPLY_LAY_6F2_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, carparkSupplyLay6F2Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply and lay Type 1, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.carparkType1Mm ?? DEFAULT_CARPARK_TYPE1_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, carparkType1Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Bindercourse, xxmm thick">
            <select
              value={BINDERCOURSE_OPTIONS.find((opt) => opt.mm === (configDraft.carparkBindercourseMm ?? DEFAULT_CARPARK_BINDERCOURSE_MM))?.label ?? ''}
              onChange={(e) => {
                const opt = BINDERCOURSE_OPTIONS.find((o) => o.label === e.target.value);
                if (opt) {
                  setConfigDraft((prev) => ({ ...prev, carparkBindercourseMm: opt.mm, carparkBindercourseLabel: opt.label }));
                } else {
                  setConfigDraft((prev) => ({ ...prev, carparkBindercourseMm: DEFAULT_CARPARK_BINDERCOURSE_MM, carparkBindercourseLabel: undefined }));
                }
              }}
              style={selectStyles}
            >
              <option value="">Select</option>
              {BINDERCOURSE_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.label}>{opt.label}</option>
              ))}
            </select>
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Material Store" columns={4}>
          <ConfigField label="Material Store size — Width (m)">
            <NumberInput
              value={configDraft.materialStoreSizeW ?? DEFAULT_MATERIAL_STORE_SIZE_W}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, materialStoreSizeW: v ?? DEFAULT_MATERIAL_STORE_SIZE_W }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Material Store size — Height (m)">
            <NumberInput
              value={configDraft.materialStoreSizeH ?? DEFAULT_MATERIAL_STORE_SIZE_H}
              onChange={(v) => setConfigDraft((prev) => ({ ...prev, materialStoreSizeH: v ?? DEFAULT_MATERIAL_STORE_SIZE_H }))}
              decimalPlaces={1}
              positiveOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply & Lay 6F2, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.materialStoreSupplyLay6F2Mm ?? DEFAULT_MATERIAL_STORE_SUPPLY_LAY_6F2_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, materialStoreSupplyLay6F2Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Supply and lay Type 1, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.materialStoreType1Mm ?? DEFAULT_MATERIAL_STORE_TYPE1_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, materialStoreType1Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Concrete Grade">
            <select
              value={configDraft.materialStoreConcreteGrade ?? DEFAULT_MATERIAL_STORE_CONCRETE_GRADE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, materialStoreConcreteGrade: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {CONCRETE_GRADE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="Concrete, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.materialStoreConcreteMm ?? DEFAULT_MATERIAL_STORE_CONCRETE_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, materialStoreConcreteMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
          <ConfigField label="Mesh Reinforcement">
            <select
              value={configDraft.materialStoreMeshType ?? DEFAULT_MATERIAL_STORE_MESH_TYPE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, materialStoreMeshType: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {MESH_TYPE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Sales Parking, tarmac drive" columns={4}>
          <ConfigField label="xxmm Base Course">
            <select
              value={configDraft.salesParkingBaseCourseLabel ?? DEFAULT_SALES_BASE_COURSE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, salesParkingBaseCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SALES_BASE_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="xxmm Binder Course">
            <select
              value={configDraft.salesParkingBinderCourseLabel ?? DEFAULT_SALES_BINDER_COURSE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, salesParkingBinderCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SALES_BINDER_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="xxmm Surface Course">
            <select
              value={configDraft.salesParkingSurfaceCourseLabel ?? DEFAULT_SALES_SURFACE_COURSE}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, salesParkingSurfaceCourseLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SALES_SURFACE_COURSE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="Blockpaved footpaths">
            <select
              value={configDraft.salesParkingBlockPavedFootpathsLabel ?? DEFAULT_SALES_BLOCK_PAVED_FOOTPATHS}
              onChange={(e) => setConfigDraft((prev) => ({ ...prev, salesParkingBlockPavedFootpathsLabel: e.target.value }))}
              style={selectStyles}
            >
              <option value="">Select</option>
              {SALES_BLOCK_PAVED_FOOTPATHS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label="Topsoiling, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.salesParkingTopsoilingMm ?? DEFAULT_SALES_TOPSOILING_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, salesParkingTopsoilingMm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Temporary Haul Road" columns={4}>
          <ConfigField label="Supply and lay 6F2, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.temporaryHaulRoadSupplyLay6F2Mm ?? DEFAULT_THR_SUPPLY_LAY_6F2_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, temporaryHaulRoadSupplyLay6F2Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
        <ConfigSection title="Walkways" showDivider={false} columns={4}>
          <ConfigField label="Supply and lay 6F2, xxxmm thick">
            <NumberInput
              value={(() => {
                const mm = configDraft.walkwaysSupplyLay6F2Mm ?? DEFAULT_WK_SUPPLY_LAY_6F2_MM;
                return mm / 1000;
              })()}
              onChange={(v) => {
                const metresValue = v ? parseFloat(v.toString()) : 0;
                const mmValue = Math.round(metresValue * 1000);
                setConfigDraft((prev) => ({ ...prev, walkwaysSupplyLay6F2Mm: mmValue }));
              }}
              positiveOnly
              decimalPlaces={3}
              zeroIntegerOnly
              style={{ width: '100%' }}
            />
          </ConfigField>
        </ConfigSection>
      </ColumnConfigModal>

      <style>{`
        .compound-card { overflow: visible !important; }
        .compound-card .ant-card-body { overflow: visible !important; }
        .compound-card .ant-table-tbody > tr > td { vertical-align: middle; }
        .compound-sticky-header { box-shadow: none; will-change: transform; transform: translateZ(0); z-index: 98 !important; }
        .compound-row-parent > td {
          background-color: #e0e7ff !important;
        }
        .compound-row-section > td {
          background-color: #eff6ff !important;
        }
        .compound-row-sub > td {
          background-color: #f3f4f6 !important;
        }
        .ant-table-thead > tr > th {
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>
    </MainLayout>
  );
};

export default Compound;
